import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Users, Shield, UserCheck, Award, MapPin, ArrowRight, Scale, Gavel, FileText } from 'lucide-react';
import { PILLARS, STATS as FALLBACK_STATS } from '../data/mock';
import { fetchStats } from '../lib/api';

const ICON_MAP = {
  BookOpen,
  Users,
  Shield,
  UserCheck,
  Award,
  MapPin,
};

const formatNumber = (n) => {
  if (n >= 1000) {
    return `${n.toLocaleString('en-IN')}+`;
  }
  return String(n);
};

const Home = () => {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    let mounted = true;
    fetchStats()
      .then((data) => {
        if (!mounted) return;
        setStats([
          { id: 1, value: '25,000+', label: 'citizens assisted', icon: 'UserCheck' },
          { id: 2, value: '7,000+', label: 'verified advocates', icon: 'Award' },
          { id: 3, value: String(data.states_covered), label: 'states covered', icon: 'MapPin' },
        ]);
      })
      .catch(() => {
        if (mounted) setStats(FALLBACK_STATS);
      });
    return () => { mounted = false; };
  }, []);

  const renderedStats = stats || FALLBACK_STATS;

  return (
    <div>
      {/* Hero Section */}
      <section className="bg-[#0F1B2D] text-[#F8F2E4] relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.08] pointer-events-none">
          <div className="absolute top-10 left-10"><Scale className="w-40 h-40" strokeWidth={0.5} /></div>
          <div className="absolute bottom-10 right-16"><Gavel className="w-32 h-32" strokeWidth={0.5} /></div>
          <div className="absolute top-1/2 right-1/4"><FileText className="w-24 h-24" strokeWidth={0.5} /></div>
        </div>

        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32 relative">
          <div className="max-w-3xl fade-up">
            <p className="text-xs tracking-[0.3em] font-bold text-[#E89530] mb-6">JUSTICE · GUIDANCE · TRUST</p>
            <h1 className="font-serif-besley text-5xl md:text-6xl lg:text-7xl leading-[1.05] mb-6">
              Indian Law,<br />
              <span className="text-[#E89530]">Simplified.</span>
            </h1>
            <p className="text-xl md:text-2xl text-[#F8F2E4] font-serif-besley leading-snug max-w-2xl mb-6">
              Understand your rights. Make informed decisions.
            </p>
            <p className="text-base md:text-lg text-[#C9BFA8] leading-relaxed max-w-2xl mb-10">
              We make the law easy to understand by explaining legal notices, documents, and procedures in plain language. Connect with experienced advocates, access affordable legal services, and get the guidance you need to protect your rights with confidence.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/free-aid"
                className="btn-orange inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-md font-medium text-[15px]"
              >
                Request Free Aid <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/find-a-lawyer"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-md font-medium text-[15px] border border-[#E8DCC0]/30 hover:border-[#E89530] hover:text-[#E89530] transition-colors"
              >
                Find a Lawyer
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Pillars Section */}
      <section className="bg-[#F8F2E4] py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <p className="text-sm text-[#5a5a5a] mb-3 font-sans-dm">Our Pillars</p>
          <h2 className="font-serif-besley text-5xl md:text-6xl text-[#0F1B2D] mb-6 leading-tight">
            Clear paths to justice
          </h2>
          <p className="text-[17px] text-[#3a3a3a] max-w-3xl mb-16 leading-relaxed">
            Navigating the Indian legal system shouldn&apos;t be an exhausting struggle. We offer three direct ways to protect your rights without high costs.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {PILLARS.map((p) => {
              const Icon = ICON_MAP[p.icon];
              return (
                <article key={p.id} className="pillar-card bg-[#EDE3CC] rounded-2xl p-8 md:p-10 flex flex-col h-full">
                  <div className="w-12 h-12 rounded-xl bg-[#E8DCC0] flex items-center justify-center mb-6">
                    <Icon className="w-6 h-6 text-[#0F1B2D]" strokeWidth={1.6} />
                  </div>
                  <p className="text-sm text-[#5a5a5a] mb-5 font-sans-dm">{p.badge}</p>
                  <h3 className="font-serif-besley text-3xl md:text-[34px] leading-[1.15] text-[#0F1B2D] mb-6">
                    {p.title}
                  </h3>
                  <p className="text-[15px] text-[#3a3a3a] leading-relaxed">
                    {p.description}
                  </p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="bg-[#EDE3CC] py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <p className="text-sm text-[#5a5a5a] text-center mb-3 font-sans-dm">Verified Impact</p>
          <h2 className="font-serif-besley text-4xl md:text-5xl text-[#0F1B2D] text-center mb-16">
            Trust built across India
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {renderedStats.map((s) => {
              const Icon = ICON_MAP[s.icon];
              return (
                <div key={s.id} className="text-center">
                  <Icon className="w-9 h-9 text-[#A36B1F] mx-auto mb-5" strokeWidth={1.6} />
                  <div className="font-serif-besley text-6xl md:text-7xl text-[#0F1B2D] mb-3">
                    {s.value}
                  </div>
                  <p className="text-[15px] text-[#3a3a3a]">{s.label}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-[#2D4373] py-24">
        <div className="max-w-3xl mx-auto px-6 text-center text-[#F8F2E4]">
          <h2 className="font-serif-besley text-4xl md:text-5xl mb-6">
            Your rights are protected
          </h2>
          <p className="text-[16px] text-[#D7DEE8] leading-relaxed mb-10 max-w-2xl mx-auto">
            Do not let intimidating legal notices overwhelm you. Speak with a verified Indian advocate who can translate your options in plain words today.
          </p>
          <Link
            to="/free-aid"
            className="btn-orange inline-flex items-center gap-2 px-8 py-3.5 rounded-md font-medium text-[15px]"
          >
            Request Free Aid
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
